/**
 * Platform-wide feature flag helpers - pure TypeScript, no React dependency.
 *
 * React hooks (useFlag, useVariant) and the FeatureFlagsContext live in
 * frontend/web/src/providers/FeatureFlagsProvider.tsx so this module can be
 * shared without pulling in @types/react at the shared layer.
 *
 * Usage outside React (pass flags explicitly):
 *   isEnabled("coaching_visible_summary", flags)
 *   getVariant("coaching_free_preview", flags)
 *
 * Usage inside React - import from the provider:
 *   import { useFlag, useVariant } from "../providers/FeatureFlagsProvider";
 *
 * Naming convention: <feature_area>_<descriptor>, lowercase snake_case.
 * Cleanup: remove code references within 90 days of completing an A/B test.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FlagValue = boolean | string;
export type FlagsMap = Record<string, FlagValue>;

export interface FeatureFlagsContextValue {
  flags: FlagsMap;
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Stateless helpers (for use outside React - pass flags explicitly)
// ---------------------------------------------------------------------------

/**
 * Boolean flag check against an explicit flags map.
 * Prefer the `useFlag` hook inside components.
 */
export function isEnabled(flagKey: string, flags: FlagsMap, defaultValue = false): boolean {
  const val = flags[flagKey];
  if (val === undefined) return defaultValue;
  if (typeof val === "boolean") return val;
  return val !== "" && val !== "false" && val !== "0";
}

/**
 * Variant check against an explicit flags map.
 * Prefer the `useVariant` hook inside components.
 */
export function getVariant(
  flagKey: string,
  flags: FlagsMap,
  defaultValue: string | null = null
): string | null {
  const val = flags[flagKey];
  if (val === undefined) return defaultValue;
  if (typeof val === "boolean") return val ? "control" : defaultValue;
  return val || defaultValue;
}
